import { ParticipantTypeEnum } from "@/enums/RecapEnums";
import useResponsive from "@/hooks/useResponsive.hook";
import { RecapParticipantsProps } from "@/interfaces/IRecap";
import { Person } from "@nexus/shared-ui";
import { Divider, Flex, Skeleton, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AttendeesModal from "./AttendeesModal";

const { Text } = Typography;

const Participants = ({
  participants,
  isLoading = false,
}: RecapParticipantsProps & {
  participants?: any;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const isResponsive = useResponsive();

  const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);
  const [attendeesModalFilters, setAttendeesModalFilters] = useState<
    ParticipantTypeEnum[]
  >([]);

  const getUserImage = useMemo(
    () => (userId?: string) => {
      if (!userId) return "";

      return `${(
        window._env_?.VITE_BASE_API_URL || import.meta.env.VITE_BASE_API_URL
      ).replace("/meet/", "/admincenter/")}/v1/users/${userId}/image`;
    },
    []
  );

  const participantsList = useMemo(
    () => participants?.data || [],
    [participants]
  );

  const attendeesList = useMemo(
    () =>
      participantsList.filter(
        (participant: any) => participant?.type === ParticipantTypeEnum.Attendee
      ),
    [participantsList]
  );

  const guestsList = useMemo(
    () =>
      participantsList.filter(
        (participant: any) => participant?.type === ParticipantTypeEnum.Guest
      ),
    [participantsList]
  );

  const joinedViaCallList = useMemo(
    () =>
      participantsList.filter(
        (participant: any) =>
          participant?.type === ParticipantTypeEnum.JoinViaCall
      ),
    [participantsList]
  );

  const openAttendeesModal = (filters: ParticipantTypeEnum[]) => {
    setAttendeesModalFilters(filters);
    setIsAttendeesModalOpen(true);
  };

  const closeAttendeesModal = () => {
    setIsAttendeesModalOpen(false);
    setAttendeesModalFilters([]);
  };

  if (isLoading) {
    return <Skeleton active paragraph={false} />;
  }

  return (
    <>
      <Flex
        className="recap-participants"
        gap={isResponsive ? 12 : 8}
        align={isResponsive ? "" : "center"}
        vertical={isResponsive}
        wrap={!isResponsive}
      >
        <Flex
          align="center"
          justify={isResponsive ? "space-between" : "flex-start"}
          gap={4}
        >
          <Text>{t("recap.organizedBy")}</Text>
          <Text className="color-primary">
            {participants?.stats?.organizer || "-"}
          </Text>
        </Flex>

        {!isResponsive ? <Divider className="m-0" type="vertical" /> : null}

        <Flex
          align="center"
          justify={isResponsive ? "space-between" : "flex-start"}
          gap={16}
          className={
            isResponsive ? "" : "recap-participants__item cursor-pointer p-2"
          }
          onClick={() =>
            openAttendeesModal([
              ParticipantTypeEnum.Attendee,
              ParticipantTypeEnum.Absent,
            ])
          }
        >
          <Text>
            {t("recap.attended", {
              presents: participants?.stats?.attendedCount || 0,
              total: participants?.stats?.invitedCount || 0,
            })}
          </Text>

          <Flex align="center">
            {attendeesList
              .slice(0, 3)
              .map((participant: any, index: number) => (
                <div
                  key={`${participant?.userId || participant?.nickname}-${index}`}
                  className="recap-participants__avatar"
                >
                  <Person
                    avatarSize={24}
                    thumbUrl={getUserImage(
                      participant?.userId || participant?.user?.id
                    )}
                    description={null}
                  />
                </div>
              ))}

            {attendeesList.length > 3 && (
              <Flex
                align="center"
                justify="center"
                className="recap-participants__extra"
              >
                +{attendeesList.length - 3}
              </Flex>
            )}
          </Flex>
        </Flex>

        {!isResponsive ? <Divider className="m-0" type="vertical" /> : null}

        <Flex
          className={
            isResponsive
              ? "cursor-pointer"
              : "recap-participants__item cursor-pointer p-2"
          }
          align="center"
          justify={isResponsive ? "space-between" : "flex-start"}
          gap={16}
          onClick={() => openAttendeesModal([ParticipantTypeEnum.Guest])}
        >
          <Text>
            {participants?.stats?.guestCount || 0}{" "}
            {t("recap.participantsGuests")}
          </Text>

          <Flex align="center">
            {guestsList.slice(0, 2).map((participant: any, index: number) => (
              <div
                key={`${participant?.userId || participant?.nickname}-${index}`}
                className="recap-participants__avatar"
              >
                <Person
                  avatarSize={24}
                  thumbUrl={getUserImage(
                    participant?.userId || participant?.user?.id
                  )}
                  description={null}
                />
              </div>
            ))}

            {guestsList.length > 2 && (
              <Flex
                align="center"
                justify="center"
                className="recap-participants__extra"
              >
                +{guestsList.length - 2}
              </Flex>
            )}
          </Flex>
        </Flex>

        {!isResponsive ? <Divider className="m-0" type="vertical" /> : null}

        <Flex
          className={
            isResponsive
              ? "cursor-pointer"
              : "recap-participants__item cursor-pointer p-2"
          }
          align="center"
          justify={isResponsive ? "space-between" : "flex-start"}
          gap={16}
          onClick={() => openAttendeesModal([ParticipantTypeEnum.JoinViaCall])}
        >
          <Text>
            {participants?.stats?.joinedViaCallCount || 0}{" "}
            {t("recap.participantJoinedViaCall")}
          </Text>

          <Flex align="center">
            {joinedViaCallList
              .slice(0, 3)
              .map((participant: any, index: number) => (
                <div
                  key={`${
                    participant?.userId || participant?.nickname
                  }-${index}`}
                  className="recap-participants__avatar"
                >
                  <Person
                    avatarSize={24}
                    thumbUrl={getUserImage(
                      participant?.userId || participant?.user?.id
                    )}
                    description={null}
                  />
                </div>
              ))}

            {joinedViaCallList.length > 3 && (
              <Flex
                align="center"
                justify="center"
                className="recap-participants__extra"
              >
                +{joinedViaCallList.length - 3}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>

      <AttendeesModal
        open={isAttendeesModalOpen}
        onClose={closeAttendeesModal}
        initialFilters={attendeesModalFilters}
        participants={participants}
      />
    </>
  );
};

export default Participants;
